import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, Play, Users, MapPin, ChevronRight, Clock, Calendar } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useLangPath } from "@/hooks/useLangPath";
import { trackEvent } from "@/lib/analytics";

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const SearchModal = ({ open, onOpenChange }: SearchModalProps) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const { t } = useTranslation();
  const lp = useLangPath();

  useEffect(() => {
    if (!open) {
      setQuery("");
      setDebouncedQuery("");
    }
  }, [open]);

  const searchTrackedRef = useRef(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300);
    return () => clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    if (debouncedQuery.trim().length >= 2 && !searchTrackedRef.current) {
      searchTrackedRef.current = true;
      trackEvent("Search Used", { query: debouncedQuery.trim() });
    }
  }, [debouncedQuery]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const { data: results, isLoading } = useQuery({
    queryKey: ["search", debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery.trim()) return { episodes: [], players: [], tournaments: [] };
      const q = `%${debouncedQuery}%`;
      const [episodesByTitle, players, tournaments] = await Promise.all([
        supabase.from("episodes")
          .select("id, slug, title, episode_number, thumbnail_url, duration_minutes, air_date, tournament_id, tournaments(name, year)")
          .ilike("title", q).order("air_date", { ascending: false }).limit(50),
        supabase.from("players")
          .select("id, slug, name, winamax_alias, photo_url, nationality, episode_players(count)")
          .or(`name.ilike.${q},winamax_alias.ilike.${q}`).limit(10),
        supabase.from("tournaments")
          .select("id, slug, name, city, year")
          .or(`name.ilike.${q},city.ilike.${q}`).limit(10),
      ]);

      const matchedPlayers = players.data ?? [];
      let allEpisodes = episodesByTitle.data ?? [];

      if (matchedPlayers.length > 0) {
        const playerIds = matchedPlayers.map((p: any) => p.id);
        const { data: epLinks } = await supabase
          .from("episode_players")
          .select("episode_id")
          .in("player_id", playerIds);

        if (epLinks && epLinks.length > 0) {
          const existingIds = new Set(allEpisodes.map((e: any) => e.id));
          const missingIds = [...new Set(epLinks.map((l: any) => l.episode_id))].filter(id => !existingIds.has(id));

          if (missingIds.length > 0) {
            const { data: extraEpisodes } = await supabase
              .from("episodes")
              .select("id, slug, title, episode_number, thumbnail_url, duration_minutes, air_date, tournament_id, tournaments(name, year)")
              .in("id", missingIds);
            if (extraEpisodes) {
              allEpisodes = [...allEpisodes, ...extraEpisodes];
            }
          }
        }
      }

      allEpisodes.sort((a: any, b: any) => {
        if (!a.air_date && !b.air_date) return 0;
        if (!a.air_date) return 1;
        if (!b.air_date) return -1;
        return new Date(b.air_date).getTime() - new Date(a.air_date).getTime();
      });

      return {
        episodes: allEpisodes,
        players: matchedPlayers,
        tournaments: tournaments.data ?? [],
      };
    },
    enabled: debouncedQuery.trim().length > 0,
  });

  const totalResults = results
    ? results.episodes.length + results.players.length + results.tournaments.length
    : 0;
  const hasResults = totalResults > 0;

  const close = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden [&>button]:hidden">
        <div className="relative border-b border-border">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="h-14 pl-14 text-sm bg-transparent border-none rounded-none focus-visible:ring-0 focus-visible:ring-offset-0"
            autoFocus
          />
          <kbd className="absolute right-4 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-1 rounded border border-border bg-muted px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            ESC
          </kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {debouncedQuery.trim() && !isLoading && (
            <p className="text-xs text-muted-foreground px-4 pt-4 pb-2">
              {t("search.resultsFor", { query: debouncedQuery, count: totalResults })}
            </p>
          )}

          {isLoading && (
            <div className="space-y-2 p-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="animate-pulse bg-muted h-14 rounded" />
              ))}
            </div>
          )}

          {!isLoading && !debouncedQuery.trim() && (
            <p className="text-center text-muted-foreground text-sm py-12">
              {t("search.typeToSearch")}
            </p>
          )}

          {!isLoading && debouncedQuery.trim() && !hasResults && (
            <p className="text-center text-muted-foreground text-sm py-12">{t("search.noResults", { query: debouncedQuery })}</p>
          )}

          {results && hasResults && (
            <div className="space-y-6 p-4">
              {results.players.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-accent" />
                    <h2 className="text-xs font-bold tracking-tight uppercase">{t("common.players")}</h2>
                    <span className="rounded-full bg-accent/20 text-accent text-[10px] font-mono font-bold px-2 py-0.5">
                      {results.players.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {results.players.map((p: any) => {
                      const epCount = p.episode_players?.[0]?.count ?? 0;
                      return (
                        <Link key={p.id} to={lp(`/joueurs/${p.slug || p.id}`)} onClick={close} className="group block">
                          <div className="flex items-center gap-3 p-2.5 rounded hover:bg-accent/5 transition-colors">
                            {p.photo_url ? (
                              <img src={p.photo_url} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-muted flex-shrink-0 flex items-center justify-center">
                                <Users className="h-3 w-3 text-muted-foreground" />
                              </div>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="text-sm font-medium group-hover:text-accent transition-colors">{p.name}</p>
                              <p className="font-mono text-[10px] text-muted-foreground">
                                {[p.nationality, epCount > 0 ? t("common.episodeCount", { count: epCount }) : null].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </section>
              )}

              {results.tournaments.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-4 w-4 text-accent" />
                    <h2 className="text-xs font-bold tracking-tight uppercase">{t("common.tournaments")}</h2>
                    <span className="rounded-full bg-accent/20 text-accent text-[10px] font-mono font-bold px-2 py-0.5">
                      {results.tournaments.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {results.tournaments.map((tt: any) => (
                      <Link key={tt.id} to={lp(`/tournois/${tt.slug || tt.id}`)} onClick={close} className="group block">
                        <div className="flex items-center gap-3 p-2.5 rounded hover:bg-accent/5 transition-colors">
                          <MapPin className="h-4 w-4 text-accent flex-shrink-0" />
                          <div className="min-w-0 flex-1">
                            <p className="text-sm font-medium group-hover:text-accent transition-colors">{tt.name}{tt.year ? ` (${tt.year})` : ""}</p>
                            {tt.city && <p className="font-mono text-[10px] text-muted-foreground">{tt.city}</p>}
                          </div>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}

              {results.episodes.length > 0 && (
                <section>
                  <div className="flex items-center gap-2 mb-3">
                    <Play className="h-4 w-4 text-accent" />
                    <h2 className="text-xs font-bold tracking-tight uppercase">{t("common.episodes")}</h2>
                    <span className="rounded-full bg-accent/20 text-accent text-[10px] font-mono font-bold px-2 py-0.5">
                      {results.episodes.length}
                    </span>
                  </div>
                  <div className="space-y-0.5">
                    {results.episodes.map((ep: any) => (
                      <Link key={ep.id} to={lp(`/episodes/${ep.slug || ep.id}`)} onClick={close} className="group block">
                        <div className="flex items-center gap-3 p-2.5 rounded hover:bg-accent/5 transition-colors">
                          {ep.thumbnail_url ? (
                            <img src={ep.thumbnail_url} alt="" className="w-16 h-10 object-cover flex-shrink-0 rounded" />
                          ) : (
                            <div className="w-16 h-10 bg-muted flex-shrink-0 flex items-center justify-center rounded">
                              <Play className="h-3 w-3 text-muted-foreground" />
                            </div>
                          )}
                          <div className="min-w-0 flex-1">
                            <span className="font-mono text-[10px] font-bold text-accent">
                              E{String(ep.episode_number).padStart(2, "0")}
                            </span>
                            <p className="text-sm font-medium truncate group-hover:text-accent transition-colors">{ep.title}</p>
                            <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                              {ep.tournaments?.name && (
                                <span className="font-mono text-[10px] text-muted-foreground">
                                  {ep.tournaments.name}{ep.tournaments.year ? ` ${ep.tournaments.year}` : ""}
                                </span>
                              )}
                              {ep.air_date && (
                                <span className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                                  <Calendar className="h-2.5 w-2.5" />
                                  {new Date(ep.air_date).toLocaleDateString("fr-FR")}
                                </span>
                              )}
                              {ep.duration_minutes && (
                                <span className="flex items-center gap-1 font-mono text-[10px] text-muted-foreground">
                                  <Clock className="h-2.5 w-2.5" />
                                  {ep.duration_minutes} {t("common.min")}
                                </span>
                              )}
                            </div>
                          </div>
                          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SearchModal;
